import { useState, useEffect, useCallback, useRef } from "react";
import { DollarSign, FileText, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { getMasterPriceHistory, getSubscriptionPlanHistory } from "@/services/masters";
import { getMasterPricesHistoryByStateCity, getSubscriptionsHistoryByStateCity } from "@/services/branchIncharge";
import { getZoneMasterPricesHistory, getZoneSubscriptionsHistory } from "@/services/zoneManager";
import GenericTable from "@/components/ui/GenericTable";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/store";
import { useRoleContext } from "@/hooks/useRoleContext";
import Pagination from "../common/Pagination";


type HistoryTab = "masterPrice" | "subscription";

/* ─── Ranges expandable badge ─────────────────────────────────── */
interface KMRange {
  KM_Price: number;
  KM_Range: string;
  KM_Percentage: number;
}

function RangesBadge({ ranges }: { ranges: KMRange[] }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  }, [open]);

  const dropdown = open ? (
    <div
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        zIndex: 9999,
      }}
      className="bg-white border border-gray-200 rounded-xl shadow-2xl p-3 min-w-[220px]"
    >
      <table className="text-xs w-full">
        <thead>
          <tr className="text-gray-500 border-b border-gray-100">
            <th className="text-left pb-1 font-semibold">Range</th>
            <th className="text-right pb-1 font-semibold">Price</th>
            <th className="text-right pb-1 font-semibold">×</th>
          </tr>
        </thead>
        <tbody>
          {ranges.map((r, i) => (
            <tr key={i} className="border-b border-gray-50 last:border-0">
              <td className="py-1 text-gray-700">{r.KM_Range}</td>
              <td className="py-1 text-right text-gray-900 font-medium">
                ₹{r.KM_Price}
              </td>
              <td className="py-1 text-right text-gray-500">
                {r.KM_Percentage}x
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors"
      >
        {ranges.length} range{ranges.length !== 1 ? "s" : ""}
        {open ? (
          <ChevronUp className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </button>

      {open && createPortal(dropdown, document.body)}
    </>
  );
}

interface RoleProps { isBranchIncharge: boolean; isZoneManager: boolean; stateCode: string; cityCode: string; zoneCode: string; }

/* ─── Master Price History ─────────────────────────────────────── */
function MasterPriceHistory({ isBranchIncharge, isZoneManager, stateCode, cityCode, zoneCode }: RoleProps) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const isInitialMount = useRef(true);

  const load = useCallback(async (p: number, q: string = "") => {
    setLoading(true);
    try {
      const res = isZoneManager && zoneCode
        ? await getZoneMasterPricesHistory(zoneCode, p)
        : isBranchIncharge && stateCode && cityCode
          ? await getMasterPricesHistoryByStateCity(stateCode, cityCode, p)
          : await getMasterPriceHistory(p, q || undefined);
      const { data } = res;
      const d = data as any;
      setRecords(d?.results ?? []);
      setTotalPages(d?.total_pages ?? 1);
      setTotalCount(d?.count ?? 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      load(1, search);
      return;
    }
    const id = setTimeout(() => { setPage(1); load(1, search); }, 500);
    return () => clearTimeout(id);
  }, [search]);

  const fmt = (iso: string) =>
    iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const columns = [
    // {
    //   key: "Version",
    //   label: "Version",
    //   render: (value: string) => (
    //     <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold bg-brand-100 text-brand-700">
    //       {value || "—"}
    //     </span>
    //   ),
    // },
    { key: "State", label: "State" },
    { key: "City", label: "District" },
    {
      key: "Base_Price",
      label: "Price",
      render: (value: string) => (
        <span className="font-semibold text-gray-900">
          ₹{value ? parseFloat(value).toLocaleString("en-IN") : "—"}
        </span>
      ),
    },
    {
      key: "Ranges",
      label: "Ranges",
      render: (value: KMRange[]) =>
        Array.isArray(value) && value.length > 0 ? <RangesBadge ranges={value} /> : "—",
    },
    { key: "Created_By", label: "Created By" },
    { key: "Updated_By", label: "Updated By" },
    {
      key: "Updated_At",
      label: "Updated At",
      render: (value: string) => (
        <span className="text-xs text-gray-500">{fmt(value)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by state, city…"
          className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
            <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={20}
        onPageChange={(p) => { setPage(p); load(p, search); }}
      />
      <GenericTable
        title="History"
        columns={columns}
        data={records}
        loading={loading}
        emptyMessage="No master price history found"
      />
    </div>
  );
}

/* ─── Subscription Plan History ───────────────────────────────── */
function SubscriptionPlanHistory({ isBranchIncharge, isZoneManager, stateCode, cityCode, zoneCode }: RoleProps) {
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const isInitialMount = useRef(true);

  const load = useCallback(async (p: number, q: string = "") => {
    setLoading(true);
    try {
      const res = isZoneManager && zoneCode
        ? await getZoneSubscriptionsHistory(zoneCode, p)
        : isBranchIncharge && stateCode && cityCode
          ? await getSubscriptionsHistoryByStateCity(stateCode, cityCode, p)
          : await getSubscriptionPlanHistory(p, q || undefined);
      const { data } = res;
      const d = data as any;
      setRecords(d?.results ?? []);
      setTotalPages(d?.total_pages ?? 1);
      setTotalCount(d?.count ?? 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      load(1, search);
      return;
    }
    const id = setTimeout(() => { setPage(1); load(1, search); }, 500);
    return () => clearTimeout(id);
  }, [search]);

  const fmt = (iso: string) =>
    iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

  const columns = [
    // {
    //   key: "Version",
    //   label: "Version",
    //   render: (value: string) => value ? (
    //     <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700">
    //       {value}
    //     </span>
    //   ) : "—",
    // },
    { key: "Plan_Name", label: "Plan" },
    { key: "State", label: "State" },
    { key: "City", label: "District" },
    {
      key: "Base_Price",
      label: "Price",
      render: (value: any) => value != null ? (
        <span className="font-semibold text-gray-900">
          ₹{parseFloat(value).toLocaleString("en-IN")}
        </span>
      ) : "—",
    },
    { key: "Created_By", label: "Created By" },
    { key: "Updated_By", label: "Updated By" },
    {
      key: "Updated_At",
      label: "Updated At",
      render: (value: string) => (
        <span className="text-xs text-gray-500">{fmt(value)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by plan, state, city…"
          className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
            <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        itemsPerPage={20}
        onPageChange={(p) => { setPage(p); load(p, search); }}
      />
      <GenericTable
        title="Subscription Plan History"
        columns={columns}
        data={records}
        loading={loading}
        emptyMessage="No subscription plan history found"
      />
    </div>
  );
}

/* ─── Main HistoryView ─────────────────────────────────────────── */
export default function HistoryView() {
  const { loaded: permsLoaded, data: permissions } = useSelector((state: RootState) => state.permissions);
  const { isBranchIncharge, isZoneManager, stateCode, cityCode, zoneCode } = useRoleContext();

  const canSeeMasterPrice  = !permsLoaded || ("masterpriceshistory"    in permissions && permissions["masterpriceshistory"]?.Can_View    !== false);
  const canSeeSub          = !permsLoaded || ("subscriptionplanshistory" in permissions && permissions["subscriptionplanshistory"]?.Can_View !== false);

  const defaultTab: HistoryTab = canSeeMasterPrice ? "masterPrice" : "subscription";
  const [activeTab, setActiveTab] = useState<HistoryTab>(defaultTab);

  const tabs = [
    // { key: "masterPrice" as HistoryTab, label: "Master Price History",      icon: <DollarSign className="w-4 h-4" />, visible: canSeeMasterPrice },
    { key: "subscription" as HistoryTab, label: "Subscription Plan History", icon: <FileText   className="w-4 h-4" />, visible: canSeeSub },
  ].filter(t => t.visible);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900">History</h1>
        <p className="text-gray-600 mt-1">View historical records of prices and subscription plans</p>
      </div>

      {/* Sub-tab nav */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex flex-row items-center justify-center gap-4 px-6 py-4 rounded-lg font-semibold transition-all ${
              activeTab === t.key
                ? "bg-brand-500 text-white shadow-lg"
                : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

        <SubscriptionPlanHistory isBranchIncharge={isBranchIncharge} isZoneManager={isZoneManager} stateCode={stateCode} cityCode={cityCode} zoneCode={zoneCode} />
    </div>
  );
}
